import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { slugify } from '../../utils/slugify';
import useServicePageCms from '../../hooks/useServicePageCms';
import ServicesHero from './ServicesHero';
import ServicePageBody from './ServicePageBody';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DepartmentIcon from '../../utils/departmentIcons';

const EmergencyServices = () => {
  const { content } = useServicePageCms('emergency-services');
  const { state } = useData();
  const { departments } = state;
  const navigate = useNavigate();

  const emergencyDepartments = departments.filter((dept) => {
    const category = dept.service_category || dept.serviceCategory;
    return category === 'Emergency Services' && (dept.is_active === true || dept.is_active === 1);
  });

  return (
    <Box>
      <ServicesHero
        title={content.title}
        subtitle={content.subtitle}
        serviceType="emergency"
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {content.introTitle}
          </Typography>
          <Typography variant="h6" paragraph sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
            {content.introText}
          </Typography>
        </Box>

        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4}>
            {emergencyDepartments.length > 0 ? (
              emergencyDepartments.map((department) => (
                <Grid item xs={12} md={6} lg={4} key={department.id}>
                  <Card sx={{
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      transition: 'transform 0.3s ease',
                      boxShadow: 6
                    }
                  }} onClick={() => navigate(`/department/${slugify(department.name)}`)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DepartmentIcon iconName={department.icon} sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                          {department.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {department.description}
                      </Typography>

                      {(department.showPhone && department.phone) || (department.showEmail && department.email) ? (
                        <Box sx={{ mt: 2 }}>
                          {department.showPhone && department.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {department.phone}
                              </Typography>
                            </Box>
                          )}
                          {department.showEmail && department.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {department.email}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    Emergency Departments
                  </Typography>
                  <Typography color="text.secondary">
                    Our emergency departments include Emergency Medicine and Critical Care.
                    We provide immediate medical attention for acute conditions and injuries.
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>

        <ServicePageBody content={content} inContainer={false} />
      </Container>
    </Box>
  );
};

export default EmergencyServices;
